import { createAsyncThunk } from '@reduxjs/toolkit';
import { ChatsActions } from 'applicaiton/constants/actions';
import { AxiosError } from 'axios';
import { ChatsAPI, ChatsSearchParams } from 'integration/api/chats';
import { MassMessageRequestDto } from '@docbay/schemas';
import { StartTeleconsultationRequest } from './models';

/**
 * Fetches the list of chats based on search parameters.
 *
 * @param params - The search parameters to filter the chats.
 */
export const getChats = createAsyncThunk(
  ChatsActions.getChats,
  async (params: ChatsSearchParams, { rejectWithValue }) => {
    try {
      const { data: responseData } = await ChatsAPI.getChats(params);
      return responseData;
    } catch (e) {
      const error = e as AxiosError;
      return rejectWithValue(error.message);
    }
  },
);

/**
 * Fetches the access token required for chat operations.
 */
export const getAccessToken = createAsyncThunk(
  ChatsActions.getAccessToken,
  async (_: void, { rejectWithValue }) => {
    try {
      const { data: responseData } = await ChatsAPI.getAccessToken();
      return responseData;
    } catch (e) {
      const error = e as AxiosError;
      return rejectWithValue(error.message);
    }
  },
);

/**
 * Creates a new chat for the specified patient.
 *
 * @param patientId - The ID of the patient to create the chat for.
 */
export const createChat = createAsyncThunk(
  ChatsActions.createChat,
  async (patientId: string, { rejectWithValue }) => {
    try {
      const { data: responseData } = await ChatsAPI.createChat(patientId);
      return responseData;
    } catch (e) {
      const error = e as AxiosError;
      return rejectWithValue(error.message);
    }
  },
);

/**
 * Posts a mass message to the specified chat.
 *
 * @param params - The parameters for the mass message request.
 */
export const postMassMessagesChat = createAsyncThunk(
  ChatsActions.postMassMessagesChat,
  async (params: MassMessageRequestDto, { rejectWithValue }) => {
    try {
      const { data: responseData } = await ChatsAPI.postMassMessagesChat({
        params,
      });
      return responseData;
    } catch (e) {
      const error = e as AxiosError;
      return rejectWithValue(error.message);
    }
  },
);

/**
 * Updates the specified chat with new data.
 *
 * @param data - The conversation SID and data to update the chat with.
 */
export const updateChat = createAsyncThunk(
  ChatsActions.updateChat,
  async (data: { conversationSID: string; data: any }, { rejectWithValue }) => {
    try {
      const { data: responseData } = await ChatsAPI.updateChat(
        data.conversationSID,
        data.data,
      );
      return responseData;
    } catch (e) {
      const error = e as AxiosError;
      return rejectWithValue(error.message);
    }
  },
);

/**
 * Archives the specified chat.
 *
 * @param conversationSID - The conversation SID of the chat to archive.
 */
export const archiveChat = createAsyncThunk(
  ChatsActions.archiveChat,
  async (conversationSID: string, { rejectWithValue }) => {
    try {
      const { data: responseData } = await ChatsAPI.archiveChat(
        conversationSID,
      );
      return responseData;
    } catch (e) {
      const error = e as AxiosError;
      return rejectWithValue(error.message);
    }
  },
);

/**
 * Unarchives the specified chat.
 *
 * @param conversationSID - The conversation SID of the chat to unarchive.
 */
export const unArchiveChat = createAsyncThunk(
  ChatsActions.unArchiveChat,
  async (conversationSID: string, { rejectWithValue }) => {
    try {
      const { data: responseData } = await ChatsAPI.unArchiveChat(
        conversationSID,
      );
      return responseData;
    } catch (e) {
      const error = e as AxiosError;
      return rejectWithValue(error.message);
    }
  },
);

/**
 * Deletes the specified chat.
 *
 * @param conversationSID - The conversation SID of the chat to delete.
 */
export const deleteChat = createAsyncThunk(
  ChatsActions.deleteChat,
  async (conversationSID: string, { rejectWithValue }) => {
    try {
      const { data: responseData } = await ChatsAPI.deleteChat(conversationSID);
      return responseData;
    } catch (e) {
      const error = e as AxiosError;
      return rejectWithValue(error.message);
    }
  },
);

/**
 * Deletes the specified mass message.
 *
 * @param massMessageId - The ID of the mass message to delete.
 */
export const deleteMassMessage = createAsyncThunk(
  ChatsActions.deleteMassMessage,
  async (massMessageId: string, { rejectWithValue }) => {
    try {
      const { data: responseData } = await ChatsAPI.deleteMassMessage(
        massMessageId,
      );
      return responseData;
    } catch (e) {
      const error = e as AxiosError;
      return rejectWithValue(error.message);
    }
  },
);

/**
 * Starts a teleconsultation for the specified chat.
 *
 * @param request - The request object containing the details for the teleconsultation.
 */
export const startChatTeleconsultation = createAsyncThunk(
  ChatsActions.startChatTeleconsultation,
  async (request: StartTeleconsultationRequest, { rejectWithValue }) => {
    try {
      const { data: responseData } = await ChatsAPI.startChatTeleconsultation(
        request,
      );
      return responseData;
    } catch (e) {
      const error = e as AxiosError;
      return rejectWithValue(error.message);
    }
  },
);

/**
 * Adds a document message to the specified chat.
 *
 * @param conversationSID - The conversation SID of the chat to add the message to.
 * @param data - The FormData object containing the document data.
 */
export const addDocumentMessage = createAsyncThunk(
  ChatsActions.addDocumentMessage,
  async (
    {
      conversationSID,
      data,
    }: {
      conversationSID: string;
      data: FormData;
    },
    { rejectWithValue },
  ) => {
    try {
      const { data: responseData } = await ChatsAPI.addDocumentMessage({
        conversationSID,
        data,
      });
      return responseData;
    } catch (e) {
      const error = e as AxiosError;
      return rejectWithValue(error.message);
    }
  },
);

/**
 * Closes the topic for the specified chat.
 *
 * @param conversationSid - The conversation SID of the chat to close the topic for.
 */
export const closeTopic = createAsyncThunk(
  ChatsActions.updateChat,
  async (conversationSid: string, { rejectWithValue }) => {
    try {
      const { data: responseData } = await ChatsAPI.closeTopic(conversationSid);
      return responseData;
    } catch (e) {
      const error = e as AxiosError;
      return rejectWithValue(error.message);
    }
  },
);
